import { splitMissingTokens } from './missingMarkers'

export type InlineNode =
  | { type: 'text'; text: string }
  | { type: 'strong'; children: InlineNode[] }
  | { type: 'em'; children: InlineNode[] }
  | { type: 'del'; children: InlineNode[] }
  | { type: 'link'; children: InlineNode[]; url: string }
  | { type: 'missing'; index: number }
  | { type: 'code'; text: string }

export type ColumnAlign = 'left' | 'right' | 'center' | null

export type MarkdownBlock =
  | { type: 'heading'; children: InlineNode[] }
  | { type: 'paragraph'; children: InlineNode[] }
  | { type: 'blockquote'; children: InlineNode[] }
  | { type: 'list'; ordered: boolean; start: number; items: InlineNode[][] }
  | {
      type: 'table'
      align: ColumnAlign[]
      header: InlineNode[][]
      rows: InlineNode[][][]
    }

const HEADING = /^#{1,6}\s+(.*)$/
const BLOCKQUOTE = /^\s*>\s?(.*)$/
const LIST_ITEM = /^\s*(?:[-*+]|(\d+)[.)])\s+(.*)$/
const TABLE_ROW = /^\s*\|.*\|\s*$/
const TABLE_SEPARATOR = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/
const INLINE_TOKEN =
  /`([^`]+)`|(!?)\[([^\]]*)\]\(([^\s)]+)(?:\s+"[^"]*")?\)|(\*\*|__|~~|\*|_)/

function pushText(out: InlineNode[], text: string): void {
  for (const part of splitMissingTokens(text)) {
    out.push(
      part.type === 'missing'
        ? { type: 'missing', index: part.index }
        : { type: 'text', text: part.text }
    )
  }
}

function pushCode(out: InlineNode[], text: string): void {
  for (const part of splitMissingTokens(text)) {
    out.push(
      part.type === 'missing'
        ? { type: 'missing', index: part.index }
        : { type: 'code', text: part.text }
    )
  }
}

function spanType(marker: string): 'strong' | 'em' | 'del' {
  if (marker === '~~') return 'del'
  return marker.length === 2 ? 'strong' : 'em'
}

function parseEmphasis(text: string, out: InlineNode[]): void {
  let rest = text
  while (rest.length > 0) {
    const match = INLINE_TOKEN.exec(rest)
    if (!match || match.index === undefined) {
      pushText(out, rest)
      return
    }
    const start = match.index
    const [, code, bang, label, url, emphasisMarker] = match
    if (code !== undefined) {
      if (start > 0) pushText(out, rest.slice(0, start))
      pushCode(out, code)
      rest = rest.slice(start + match[0].length)
      continue
    }
    if (url !== undefined) {
      if (start > 0) pushText(out, rest.slice(0, start))
      if (bang !== '!') {
        const children: InlineNode[] = []
        parseEmphasis(label ?? '', children)
        out.push({ type: 'link', children, url })
      }
      rest = rest.slice(start + match[0].length)
      continue
    }
    const marker = emphasisMarker ?? ''
    const close = rest.indexOf(marker, start + marker.length)
    if (close < 0 || close === start + marker.length) {
      pushText(out, rest.slice(0, start + marker.length))
      rest = rest.slice(start + marker.length)
      continue
    }
    if (start > 0) pushText(out, rest.slice(0, start))
    const inner: InlineNode[] = []
    parseEmphasis(rest.slice(start + marker.length, close), inner)
    out.push({ type: spanType(marker), children: inner })
    rest = rest.slice(close + marker.length)
  }
}

/**
 * Inline markdown after `***` tokenisation: bold, italic, strikethrough, code
 * spans, links, and missing markers wherever they sit, code spans included;
 * images are dropped.
 */
export function parseInline(text: string): InlineNode[] {
  const out: InlineNode[] = []
  parseEmphasis(text, out)
  return out
}

function splitTableRow(line: string): string[] {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '')
  return trimmed
    .split(/(?<!\\)\|/)
    .map((cell) => cell.trim().replace(/\\\|/g, '|'))
}

function parseAlignments(line: string): ColumnAlign[] {
  return splitTableRow(line).map((cell) => {
    const left = cell.startsWith(':')
    const right = cell.endsWith(':')
    if (left && right) return 'center'
    if (right) return 'right'
    if (left) return 'left'
    return null
  })
}

type OpenBlock =
  | { type: 'paragraph' | 'blockquote'; lines: string[] }
  | { type: 'list'; ordered: boolean; start: number; items: string[] }

function closeBlock(open: OpenBlock): MarkdownBlock {
  if (open.type === 'list') {
    return {
      type: 'list',
      ordered: open.ordered,
      start: open.start,
      items: open.items.map(parseInline),
    }
  }
  return { type: open.type, children: parseInline(open.lines.join(' ')) }
}

function parseTable(
  lines: readonly string[],
  index: number
): { block: MarkdownBlock; end: number } | undefined {
  const line = lines[index] ?? ''
  const separator = lines[index + 1] ?? ''
  if (!TABLE_ROW.test(line) || !TABLE_SEPARATOR.test(separator)) return
  const header = splitTableRow(line).map(parseInline)
  const align = parseAlignments(separator)
  const rows: InlineNode[][][] = []
  let end = index + 2
  while (end < lines.length && TABLE_ROW.test(lines[end] ?? '')) {
    rows.push(splitTableRow(lines[end] ?? '').map(parseInline))
    end += 1
  }
  return { block: { type: 'table', align, header, rows }, end }
}

function blockAccumulator() {
  const blocks: MarkdownBlock[] = []
  let open: OpenBlock | undefined
  const close = () => {
    if (open) blocks.push(closeBlock(open))
    open = undefined
  }
  return {
    blocks,
    close,
    push(block: MarkdownBlock) {
      close()
      blocks.push(block)
    },
    appendLine(type: 'paragraph' | 'blockquote', line: string) {
      if (open?.type !== type) {
        close()
        open = { type, lines: [] }
      }
      open.lines.push(line)
    },
    appendListItem(number: string | undefined, item: string) {
      const ordered = number !== undefined
      if (open?.type !== 'list' || open.ordered !== ordered) {
        close()
        open = {
          type: 'list',
          ordered,
          start: ordered ? Number(number) : 1,
          items: [],
        }
      }
      open.items.push(item)
    },
    continueListItem(text: string): boolean {
      if (open?.type !== 'list') return false
      open.items[open.items.length - 1] += ` ${text}`
      return true
    },
  }
}

/** Block structure of the document kind's markdown subset: headings, paragraphs, block quotes, lists, pipe tables. */
export function parseMarkdownBlocks(text: string): MarkdownBlock[] {
  const lines = text.replace(/\r\n?/g, '\n').split('\n')
  const acc = blockAccumulator()
  let index = 0
  while (index < lines.length) {
    const line = lines[index] ?? ''
    const trimmed = line.trim()
    const heading = HEADING.exec(trimmed)
    const quoted = BLOCKQUOTE.exec(line)
    const table = quoted ? undefined : parseTable(lines, index)
    const item = quoted || table ? null : LIST_ITEM.exec(line)
    if (trimmed.length === 0) acc.close()
    else if (heading) {
      acc.push({ type: 'heading', children: parseInline(heading[1] ?? '') })
    } else if (quoted) acc.appendLine('blockquote', (quoted[1] ?? '').trim())
    else if (table) {
      acc.push(table.block)
      index = table.end
      continue
    } else if (item) acc.appendListItem(item[1], item[2] ?? '')
    else if (!/^\s+\S/.test(line) || !acc.continueListItem(trimmed)) {
      acc.appendLine('paragraph', trimmed)
    }
    index += 1
  }
  acc.close()
  return acc.blocks
}

export function inlineText(nodes: readonly InlineNode[]): string {
  return nodes
    .map((node) => {
      if (node.type === 'text' || node.type === 'code') return node.text
      if (node.type === 'missing') return '***'
      return inlineText(node.children)
    })
    .join('')
}
